<template>
  <div>
    <validation-observer
      ref="formValidation"
      v-slot="{ handleSubmit, invalid }"
    >
      <b-form
        novalidate
        @submit.stop.prevent="handleSubmit(showConfirmSubmit)"
      >
        <MainSection
          :categories="categoryList"
          :countries="countries"
          @category-changed="onCategoryChanged"
        />
        
        <Destination />

        <DestinationSchedulingCapacity />

        <AlertProgrammaticTarget
          class="mx-2"
          :routing-type="buyerCampaign.routing.type"
        />

        <TrafficSources :traffic-sources="allowedTrafficSources" />

        <Audience />

        <!-- Integrations is for configuring Pixels, but maybe not required for MVP: Adam -->
        <!-- <Integrations /> -->

        <Budget
          :invalid="invalid"
          :minimum-initial-deposit-satisfied="isMinimumInitialDepositRequirementSatisfied"
          :loading="isLoading"
          :selected-category="selectedCategory"
          @submit="showConfirmSubmit"
        />
      </b-form>
    </validation-observer>

    <b-modal
      id="confirm-create-campaign-modal"
      ref="confirm-create-campaign-modal"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      title="Confirm Campaign Creation"
      button-size="sm"
      no-close-on-backdrop
      @hidden="termsAccepted = false"
    >
      <p>
        Agents must service all qualified calls in good faith. Valid calls disconnected or rejected to avoid payment or due to agent mishandling may be deemed acceptable and billable. Repeated violations may result in force-billing, suspension, or termination of service.
      </p>

      <b-form-checkbox
        v-model="termsAccepted"
        data-cy="checkbox-accept-terms"
        class="mb-0"
      >
        I acknowledge and accept the above terms regarding the handling of qualified calls and the potential consequences of non-compliance.
      </b-form-checkbox>

      <template #modal-footer>
        <b-button
          type="button"
          variant="secondary"
          size="sm"
          :disabled="isLoading"
          @click="$bvModal.hide('confirm-create-campaign-modal')"
        >
          Cancel
        </b-button>

        <BaseButtonLoader
          :loading="isLoading"
          class="ml-2"
          type="button"
          :custom-button="false"
          size="sm"
          variant="primary"
          :disabled="!termsAccepted"
          data-cy="button-confirm-create-campaign"
          @click="submitForm"
        >
          Confirm
        </BaseButtonLoader>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import MainSection from '../campaign/MainSection.vue'
import Destination from '../campaign/Destination.vue'
import DestinationSchedulingCapacity from '../campaign/DestinationSchedulingCapacity.vue'
import TrafficSources from '../campaign/TrafficSources.vue'
import Audience from './Audience.vue'
import Budget from './Budget.vue'
import AlertProgrammaticTarget from './AlertProgrammaticTarget.vue'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'
import { ACCOUNT_REQUIREMENTS, dashboardNotificationForAccountRequirementsMessages } from '@/store/modules/auth.store'

export default {
  name: 'BuyerCreateCampaign',

  title: 'Create campaign',

  components: {
    MainSection,
    Destination,
    DestinationSchedulingCapacity,
    TrafficSources,
    Audience,
    Budget,
    AlertProgrammaticTarget,
  },

  props: {
    id: {
      type: String,
      default: ''
    },
  },

  data() {
    return {
      isLoading: false,
      selectedCategory: null,
      termsAccepted: false
    }
  },

  computed: {
    ...mapState('common-entities', ['countries', 'categories']),

    ...mapGetters('account', ['allowedTrafficSources', 'allowedCategories']),

    ...mapState('campaign', ['buyerCampaign']),

    ...mapGetters('auth', ['isMinimumInitialDepositRequirementSatisfied']),

    categoryList() {
      //merge without duplicates
      const publicCategories = this.categories.filter(
        publicCategory => !this.allowedCategories.some(
          allowedCategory => allowedCategory.id === publicCategory.id
        )
      )
      const mergedCategories = this.allowedCategories.concat(publicCategories)

      return mergedCategories.sort((a, b) => a.name.localeCompare(b.name))
    },

  },

  watch: {
    isMinimumInitialDepositRequirementSatisfied: {
      immediate: true,
      handler(newValue) {
        if (!newValue) {
          this.showDashboardNotification(dashboardNotificationForAccountRequirementsMessages[ACCOUNT_REQUIREMENTS.MinimumInitialDeposit])
        } else {
          this.hideDashboardNotification()
        }
      }
    }
  },

  async created() {
    this.setDefaultBuyerCampaign()
    await this.getCountries()
    await this.getCategories()
  },

  methods: {
    ...mapActions([
      'showDashboardNotification',
      'hideDashboardNotification'
    ]),

    ...mapActions('common-entities', ['getCountries','getCategories']),

    ...mapActions('campaign', ['setDefaultBuyerCampaign', 'createBuyerCampaign', 'fetchBuyerCampaign']),

    onCategoryChanged(category) {
      this.selectedCategory = category
    },

    showConfirmSubmit() {
      this.termsAccepted = false
      this.$bvModal.show('confirm-create-campaign-modal')
    },

    async submitForm() {
      try {
        this.isLoading = true

        await this.createBuyerCampaign(this.buyerCampaign)

        this.$bvModal.hide('confirm-create-campaign-modal')

        // TODO: Show a detailed message about the approval process
        // and redirect to campaigns list page
        showSuccessMessage('Campaign created successfully')
        this.$router.push({ name: 'ListBuyerCampaigns' })
      } catch (error) {
        showErrorMessage(error.response?.data?.title)
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped></style>
